import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useIntl } from '@zenbusiness/application-commons-intl';

import { formatSearchParams } from '#client/utils/formatString';

import { WEBINAR_SEARCH_PARAM } from '../constants';

/**
 * Custom React hook to manage the state and behavior of a webinar dialog based on URL search parameters.
 *
 * @param {Array<object>} webinars - An array of webinar objects, each containing at least a `titleKey` property.
 * @returns {object} An object containing:
 *   - {Function} onClose: Function to clear the 'webinar' search parameter from the URL.
 *   - {Object|null} webinar: The currently selected webinar object, or null if none is selected.
 *   - {boolean} isOpen: Indicates whether the webinar dialog should be open.
 */
function useWebinarDialog(webinars = []) {
  const { formatMessage: fm } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const webinarSlug = searchParams.get(WEBINAR_SEARCH_PARAM);

  const clearWebinarParam = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(WEBINAR_SEARCH_PARAM);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const webinar = useMemo(() => {
    if (!webinarSlug || webinars.length === 0) {
      return null;
    }

    const foundWebinar = webinars.find((w) => formatSearchParams(fm({ id: w.titleKey })) === webinarSlug);
    return foundWebinar || null;
  }, [webinars, fm, webinarSlug]);

  return {
    onClose: clearWebinarParam,
    webinar,
    isOpen: Boolean(webinar),
  };
}

export default useWebinarDialog;
